import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
} from '@mui/material';
import { useModalOpenRegistration } from '../../contexts/ModalOpenContext';

const MAX_PROJECT_NAME_LENGTH = 128;

interface ProjectRenameDialogProps {
  open: boolean;
  onClose: () => void;
  currentName: string;
  onRename: (newName: string) => Promise<void>;
}

const ProjectRenameDialog: React.FC<ProjectRenameDialogProps> = ({
  open,
  onClose,
  currentName,
  onRename,
}) => {
  useModalOpenRegistration(open);
  const [newName, setNewName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  useEffect(() => {
    if (open) {
      setNewName(currentName);
      setError(null);
    }
  }, [open, currentName]);

  const trimmedName = newName.trim();
  const isUnchanged = trimmedName === currentName.trim();
  const exceedsMaxLength = trimmedName.length > MAX_PROJECT_NAME_LENGTH;
  const isInvalid = !trimmedName || exceedsMaxLength;

  const handleRename = async () => {
    if (!trimmedName) {
      setError('Project name is required');
      return;
    }

    if (exceedsMaxLength) {
      setError(`Project name must be ${MAX_PROJECT_NAME_LENGTH} characters or less`);
      return;
    }

    if (isUnchanged) {
      onClose();
      return;
    }

    setIsRenaming(true);
    setError(null);

    try {
      await onRename(trimmedName);
      onClose();
    } catch (err) {
      const errorMessage =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Failed to rename project';
      setError(errorMessage);
    } finally {
      setIsRenaming(false);
    }
  };

  return (
    <Dialog open={open} onClose={isRenaming ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rename Project</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Project Name"
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isRenaming && !isInvalid && !isUnchanged) {
                e.preventDefault();
                void handleRename();
              }
            }}
            required
            fullWidth
            autoFocus
            disabled={isRenaming}
            error={exceedsMaxLength}
            inputProps={{ maxLength: MAX_PROJECT_NAME_LENGTH }}
            helperText={
              exceedsMaxLength
                ? `Maximum ${MAX_PROJECT_NAME_LENGTH} characters`
                : `${trimmedName.length}/${MAX_PROJECT_NAME_LENGTH} characters`
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isRenaming}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleRename()}
          variant="contained"
          disabled={isRenaming || isInvalid || isUnchanged}
        >
          {isRenaming ? 'Renaming...' : 'Rename'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectRenameDialog;
